// Languages — the dynamic locale registry behind catalog translations
// (SM-283, extended by SM-314). Rows live in market-service's
// `languages` table and are proxied under /admin/catalog/languages;
// every ENABLED row becomes a tab in the per-entity TranslationsEditor
// and a choice in the trader app's language picker.
//
// SM-314 adds the two things operators were missing: editing a record
// (including renaming its locale code) and reordering the registry.
//
//   * Edit — a dialog over Code / Name / Native name / Order / Enabled.
//     A code change is a MIGRATION, not a relabel: market-service moves
//     every *_translations row from the old locale to the new one and
//     repoints the registry PK in ONE transaction. That is
//     destructive-adjacent, so the dialog makes the operator confirm a
//     second time with old → new spelled out.
//   * Reorder — accessible Move up / Move down buttons that PATCH
//     display_order, plus the numeric Order field in the dialog. A move
//     renormalizes the list to a contiguous 0..n-1 sequence and PATCHes
//     only the rows whose order actually changed (same posture as
//     MarketsOrderList), which keeps working when the stored orders have
//     gaps or collide. Disabled languages reorder like any other row —
//     disabling never touches translations or position.
//
// English is canonical: it cannot be renamed, disabled, or moved off
// position 0, and nothing may be moved above it. The server refuses all
// three (MARKET_000704) — the UI simply doesn't offer them, so the
// operator never aims at a wall.
//
// Adding or renaming a language needs no release — codes are validated
// client-side with the same canonicalization market-service applies (see
// lib/locale.ts) and duplicates are caught against the loaded list;
// server-side errors surface verbatim in the dialog. Mutations gate on
// CATEGORY_UPDATE, the same perm the language endpoints check.
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { FormModal } from "@/components/ui/FormModal";
import { Input } from "@/components/ui/Input";
import { Skeleton } from "@/components/ui/Skeleton";
import { PublishSwitch } from "@/components/markets/PublishSwitch";
import {
  createLanguage,
  keys,
  listLanguages,
  updateLanguage,
  type UpdateLanguageInput,
} from "@/lib/queries";
import type { LanguageItem } from "@/lib/types";
import { canonicalizeLocale } from "@/lib/locale";
import { useMyPerms } from "@/hooks/useMyPerms";
import { Perm } from "@/lib/perms";
import { cn } from "@/lib/cn";

// Table cell classes — same shape as the config tables elsewhere in the
// console (wallet-ops configShared) so the registry reads like the rest
// of the admin tables.
const th =
  "px-3 py-3 text-left text-2xs font-medium uppercase tracking-wide text-muted";
const thRight =
  "px-3 py-3 text-right text-2xs font-medium uppercase tracking-wide text-muted";
const td = "px-3 py-3 text-xs";

export const Route = createFileRoute("/_app/languages")({
  component: LanguagesPage,
});

// The registry is served in display_order already; sorting locally with
// the same (display_order, code) tiebreak the server uses keeps the
// Move up/down index arithmetic honest even when orders collide.
function sortRows(rows: LanguageItem[]): LanguageItem[] {
  return [...rows].sort(
    (a, b) => a.display_order - b.display_order || a.code.localeCompare(b.code),
  );
}

function LanguagesPage() {
  const qc = useQueryClient();
  const perms = useMyPerms();
  // Optimistic while perms load (same posture as the nav) — the server
  // still rejects unauthorized writes.
  const canManage =
    !perms.loaded || perms.isSuperAdmin || perms.has(Perm.CategoryUpdate);

  const languages = useQuery({
    queryKey: keys.languages,
    queryFn: listLanguages,
  });
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<LanguageItem | null>(null);

  // Every language write can change what the per-entity translation tabs
  // show (a rename repoints stored rows, enabling/disabling adds or
  // removes a tab, a reorder changes the tab order), so both families
  // are invalidated together.
  const refresh = () => {
    qc.invalidateQueries({ queryKey: keys.languages });
    qc.invalidateQueries({ queryKey: keys.catalog });
  };

  const toggle = useMutation({
    mutationFn: ({ code, enabled }: { code: string; enabled: boolean }) =>
      updateLanguage(code, { enabled }),
    onSuccess: refresh,
    meta: { successMessage: "Language updated." },
  });

  // Sequential, not parallel: each PATCH writes a before/after audit row
  // server-side, and a deterministic order keeps that trail readable.
  // Refetch on SETTLED, not just success — a move is several PATCHes, so
  // a failure halfway leaves the server holding an order the table would
  // otherwise keep lying about.
  const reorder = useMutation({
    mutationFn: async (moves: { code: string; display_order: number }[]) => {
      for (const m of moves) {
        await updateLanguage(m.code, { display_order: m.display_order });
      }
    },
    onSettled: refresh,
    meta: { successMessage: "Language order saved." },
  });

  const rows = sortRows(languages.data ?? []);
  const enIndex = rows.findIndex((l) => l.code === "en");

  const move = (from: number, to: number) => {
    const next = [...rows];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    const changed = next
      .map((l, i) => ({ code: l.code, display_order: i }))
      .filter((m, i) => next[i].display_order !== m.display_order);
    if (changed.length > 0) reorder.mutate(changed);
  };

  const busy = reorder.isPending;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl tracking-tight">Languages</h1>
        <p className="text-sm text-muted">
          The locales the catalog can be translated into. Enabled languages
          appear as translation tabs on categories, events, markets and
          outcomes, and in the trader app&apos;s language picker. English is
          the canonical content and always stays on, first.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Registry</CardTitle>
          {canManage && (
            <Button size="sm" onClick={() => setAddOpen(true)}>
              Add language
            </Button>
          )}
        </CardHeader>
        <CardBody className="p-0">
          {languages.isLoading && (
            <div className="space-y-2 p-5">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-10" />
              ))}
            </div>
          )}
          {languages.isError && (
            <p className="px-5 py-10 text-center text-sm text-danger">
              Failed to load languages.
            </p>
          )}
          {!languages.isLoading && !languages.isError && rows.length === 0 && (
            <p className="px-5 py-10 text-center text-sm text-muted">
              No languages registered.
            </p>
          )}
          {rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-line-1">
                  <tr>
                    <th className={`${th} px-5`}>Code</th>
                    <th className={th}>Name</th>
                    <th className={th}>Native name</th>
                    <th className={thRight}>Order</th>
                    <th className={th}>Enabled</th>
                    {canManage && (
                      <th className={`${thRight} pr-5`}>Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((l, i) => {
                    const isEN = l.code === "en";
                    // English is pinned to the top: it never moves, and
                    // nothing may be moved above it.
                    const canUp = !isEN && i > 0 && i - 1 !== enIndex;
                    const canDown = !isEN && i < rows.length - 1;
                    return (
                      <tr
                        key={l.code}
                        className="border-b border-line-1/60 last:border-0"
                      >
                        <td className={`${td} px-5 font-mono text-t-1`}>
                          {l.code}
                          {isEN && (
                            <Badge tone="muted" className="ml-2">
                              default
                            </Badge>
                          )}
                        </td>
                        <td className={`${td} font-medium text-t-1`}>
                          {l.name}
                        </td>
                        <td className={td}>{l.native_name}</td>
                        <td className={`${td} text-right font-mono`}>
                          {l.display_order}
                        </td>
                        <td className={td}>
                          <PublishSwitch
                            published={l.enabled}
                            label={l.enabled ? "Enabled" : "Disabled"}
                            disabled={isEN || !canManage}
                            loading={
                              toggle.isPending &&
                              toggle.variables?.code === l.code
                            }
                            onChange={(next) =>
                              toggle.mutate({ code: l.code, enabled: next })
                            }
                          />
                        </td>
                        {canManage && (
                          <td className={`${td} pr-5`}>
                            <div className="flex items-center justify-end gap-1">
                              <MoveButton
                                dir="up"
                                name={l.name}
                                disabled={!canUp || busy}
                                onClick={() => move(i, i - 1)}
                              />
                              <MoveButton
                                dir="down"
                                name={l.name}
                                disabled={!canDown || busy}
                                onClick={() => move(i, i + 1)}
                              />
                              <Button
                                size="sm"
                                variant="ghost"
                                aria-label={`Edit ${l.name}`}
                                onClick={() => setEditing(l)}
                              >
                                Edit
                              </Button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {reorder.isError && (
            <p className="px-5 pb-4 text-2xs text-no">
              Couldn&rsquo;t save the new order:{" "}
              {(reorder.error as Error).message}
            </p>
          )}
        </CardBody>
      </Card>

      <AddLanguageDialog open={addOpen} onClose={() => setAddOpen(false)} />
      {editing && (
        <EditLanguageDialog
          // Remount per row so the draft never leaks between languages.
          key={editing.code}
          language={editing}
          all={rows}
          onClose={() => setEditing(null)}
          onSaved={refresh}
        />
      )}
    </div>
  );
}

// Move up / Move down — a real <button> (keyboard-reachable, focus ring
// from .btn) with a spelled-out accessible name; the glyph is decorative.
// Disabled at the boundaries rather than silently no-op'ing, so the
// operator can see where a row can and can't go.
function MoveButton({
  dir,
  name,
  disabled,
  onClick,
}: {
  dir: "up" | "down";
  name: string;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={`Move ${name} ${dir}`}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "inline-flex h-7 w-7 items-center justify-center rounded-md border border-line-1 text-xs text-t-2 transition-colors",
        "focus:outline-none focus-visible:ring-4 focus-visible:ring-neon/15",
        disabled
          ? "cursor-not-allowed opacity-40"
          : "hover:border-line-3 hover:bg-bg-3",
      )}
    >
      <span aria-hidden>{dir === "up" ? "↑" : "↓"}</span>
    </button>
  );
}

function AddLanguageDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [nativeName, setNativeName] = useState("");
  const [displayOrder, setDisplayOrder] = useState("");

  const canonical = canonicalizeLocale(code);
  const codeError =
    code.trim() !== "" && canonical === null
      ? "Invalid code — use a BCP-47-style tag like th, zh-CN or pt-BR."
      : null;

  const reset = () => {
    setCode("");
    setName("");
    setNativeName("");
    setDisplayOrder("");
  };

  const create = useMutation({
    mutationFn: () =>
      createLanguage({
        code: canonical!,
        name: name.trim(),
        native_name: nativeName.trim() || undefined,
        display_order:
          displayOrder.trim() === "" ? undefined : Number(displayOrder),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.languages });
      qc.invalidateQueries({ queryKey: keys.catalog });
      reset();
      onClose();
    },
    meta: { successMessage: "Language added." },
  });

  const canSubmit = canonical !== null && name.trim() !== "";

  return (
    <FormModal
      open={open}
      title="Add language"
      description="Registers a new locale for catalog translations. It starts enabled — operators can begin translating immediately."
      confirmLabel="Add language"
      canSubmit={canSubmit}
      loading={create.isPending}
      error={create.isError ? (create.error as Error).message : null}
      onSubmit={() => create.mutate()}
      onCancel={() => {
        create.reset();
        reset();
        onClose();
      }}
    >
      <div className="grid grid-cols-2 gap-3">
        <Input
          label="Code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="pt-BR"
          spellCheck={false}
          className="font-mono"
          error={codeError}
          hint={
            canonical && canonical !== code.trim()
              ? `Will be stored as ${canonical}.`
              : "BCP-47-style: language, optional Script / REGION subtags."
          }
        />
        <Input
          label="Display order"
          value={displayOrder}
          onChange={(e) => setDisplayOrder(e.target.value.replace(/[^0-9]/g, ""))}
          inputMode="numeric"
          placeholder="0"
          hint="Position in pickers — lower sorts first."
        />
      </div>
      <Input
        label="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Portuguese (Brazil)"
        hint="English display name, shown in this console."
      />
      <Input
        label="Native name"
        value={nativeName}
        onChange={(e) => setNativeName(e.target.value)}
        placeholder="Português"
        hint="The language's own name for itself, shown to traders."
      />
    </FormModal>
  );
}

// Edit dialog. Two steps, and the second only appears when the operator
// actually changed the code: a rename migrates every stored translation
// for that locale, so the consequence is spelled out (old → new, atomic)
// before the PATCH goes out.
function EditLanguageDialog({
  language,
  all,
  onClose,
  onSaved,
}: {
  language: LanguageItem;
  all: LanguageItem[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEN = language.code === "en";
  const [code, setCode] = useState(language.code);
  const [name, setName] = useState(language.name);
  const [nativeName, setNativeName] = useState(language.native_name ?? "");
  const [displayOrder, setDisplayOrder] = useState(
    String(language.display_order),
  );
  const [enabled, setEnabled] = useState(language.enabled);
  const [step, setStep] = useState<"form" | "confirm">("form");

  // English's identity is its code, so its code / order / enabled are
  // frozen; the labels stay editable.
  const canonical = isEN ? language.code : canonicalizeLocale(code);
  // codeDiffers drives the confirm button's LABEL: once the operator has
  // typed a different code the button keeps promising a review step
  // instead of flickering back to "Save" while the draft is invalid.
  // codeChanged drives BEHAVIOUR and only trusts a code that actually
  // canonicalizes — and submission is blocked while codeError is set, so
  // the two can only disagree on a draft that can't be submitted anyway.
  const codeDiffers = !isEN && canonical !== language.code;
  const codeChanged =
    !isEN && canonical !== null && canonical !== language.code;
  // Duplicate detection against the loaded registry — the server checks
  // again (MARKET_000702), this just saves the round-trip.
  const duplicate =
    codeChanged &&
    all.some(
      (l) =>
        l.code !== language.code &&
        l.code.toLowerCase() === canonical!.toLowerCase(),
    );

  const codeError = isEN
    ? null
    : code.trim() === ""
      ? "Code is required."
      : canonical === null
        ? "Invalid code — use a BCP-47-style tag like th, zh-CN or pt-BR."
        : duplicate
          ? `${canonical} is already registered — pick a different code.`
          : null;
  const nameError = name.trim() === "" ? "Name is required." : null;
  const orderError =
    !isEN && displayOrder.trim() === ""
      ? "Order is required — 0 or higher."
      : null;

  const orderChanged =
    !isEN && Number(displayOrder) !== language.display_order;
  const dirty =
    codeChanged ||
    name.trim() !== language.name ||
    nativeName.trim() !== (language.native_name ?? "") ||
    orderChanged ||
    (!isEN && enabled !== language.enabled);

  const body: UpdateLanguageInput = {};
  if (codeChanged) body.code = canonical!;
  if (name.trim() !== language.name) body.name = name.trim();
  if (nativeName.trim() !== (language.native_name ?? "")) {
    body.native_name = nativeName.trim();
  }
  if (orderChanged) body.display_order = Number(displayOrder);
  if (!isEN && enabled !== language.enabled) body.enabled = enabled;

  const save = useMutation({
    mutationFn: () => updateLanguage(language.code, body),
    onSuccess: () => {
      onSaved();
      onClose();
    },
    meta: { successMessage: "Language updated." },
  });

  const canSubmit =
    dirty && !codeError && !nameError && !orderError && !save.isPending;

  const confirming = step === "confirm";

  return (
    <FormModal
      open
      title={confirming ? "Change language code?" : `Edit ${language.name}`}
      description={
        confirming
          ? undefined
          : "Changes apply to this console, the translation tabs and the trader app's language picker."
      }
      confirmLabel={
        confirming ? "Rename and migrate" : codeDiffers ? "Review change" : "Save"
      }
      cancelLabel={confirming ? "Back" : "Cancel"}
      canSubmit={canSubmit}
      loading={save.isPending}
      // Server copy verbatim — MARKET_000702/000704 messages say exactly
      // what was refused and why.
      error={save.isError ? (save.error as Error).message : null}
      onSubmit={() => {
        if (codeChanged && !confirming) {
          setStep("confirm");
          return;
        }
        save.mutate();
      }}
      onCancel={() => {
        if (confirming) {
          setStep("form");
          return;
        }
        save.reset();
        onClose();
      }}
    >
      {confirming ? (
        <div className="space-y-3">
          <div className="rounded-md border border-warn/40 bg-warn/5 p-3">
            <div className="flex items-center gap-2 font-mono text-sm text-t-1">
              <span>{language.code}</span>
              <span aria-hidden className="text-t-4">
                →
              </span>
              <span className="text-neon">{canonical}</span>
            </div>
            <p className="mt-2 text-2xs leading-relaxed text-muted">
              Every existing translation stored under{" "}
              <span className="font-mono">{language.code}</span> — categories,
              events, markets and outcomes — is migrated to{" "}
              <span className="font-mono">{canonical}</span> in a single
              transaction. Either all of it moves or none of it does; no
              translation is lost or orphaned. Traders on the old code will see
              the new one after their next load.
            </p>
          </div>
          {(body.name !== undefined ||
            body.native_name !== undefined ||
            body.display_order !== undefined ||
            body.enabled !== undefined) && (
            <p className="text-2xs text-muted">
              Saved in the same request:{" "}
              {[
                body.name !== undefined ? `name → ${body.name}` : null,
                body.native_name !== undefined
                  ? `native name → ${body.native_name || "—"}`
                  : null,
                body.display_order !== undefined
                  ? `order → ${body.display_order}`
                  : null,
                body.enabled !== undefined
                  ? `enabled → ${body.enabled ? "yes" : "no"}`
                  : null,
              ]
                .filter(Boolean)
                .join(", ")}
              .
            </p>
          )}
        </div>
      ) : (
        <>
          {isEN && (
            <p className="rounded-md border border-line-1 bg-bg-2 px-3 py-2 text-2xs leading-relaxed text-muted">
              English is the fallback locale — its code, order and enabled
              state are fixed. Name and native name stay editable.
            </p>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              disabled={isEN}
              spellCheck={false}
              className="font-mono"
              error={codeError}
              hint={
                isEN
                  ? "Fixed — en is the fallback locale."
                  : canonical && canonical !== code.trim()
                    ? `Will be stored as ${canonical}.`
                    : codeChanged
                      ? "Renaming migrates every stored translation to the new code."
                      : "BCP-47-style: language, optional Script / REGION subtags."
              }
            />
            <Input
              label="Order"
              value={displayOrder}
              onChange={(e) =>
                setDisplayOrder(e.target.value.replace(/[^0-9]/g, ""))
              }
              disabled={isEN}
              inputMode="numeric"
              error={orderError}
              hint={
                isEN
                  ? "Fixed — English always sorts first."
                  : "Position in pickers — lower sorts first."
              }
            />
          </div>
          <Input
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={nameError}
            hint="English display name, shown in this console."
          />
          <Input
            label="Native name"
            value={nativeName}
            onChange={(e) => setNativeName(e.target.value)}
            hint="The language's own name for itself, shown to traders."
          />
          <div className="flex items-center justify-between gap-3 rounded-md border border-line-1 bg-bg-2 px-3 py-2">
            <div>
              <p className="text-2xs font-semibold uppercase tracking-wide text-t-3">
                Enabled
              </p>
              <p className="mt-0.5 text-2xs text-t-4">
                {isEN
                  ? "Fixed — English is always on."
                  : "Disabling keeps every stored translation and this position; it only hides the language from the tabs and the picker."}
              </p>
            </div>
            <PublishSwitch
              published={enabled}
              label={enabled ? "Enabled" : "Disabled"}
              disabled={isEN}
              onChange={setEnabled}
            />
          </div>
        </>
      )}
    </FormModal>
  );
}
